import type { Region } from '~~/core/entities/Region';

import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { getUrlFromStaticDNS } from '@/utils';
import { getRegionMetadata } from './utils';

const regionName = 'Jardim Paulista';

const metaTags = getRegionMetadata(regionName, 'São Paulo', { gender: 'male' });

const region: Region = {
	name: regionName,
	state: 'sp',
	city: 'São Paulo',
	path: '/bairros/jardim-paulista',
	image: getUrlFromStaticDNS('/images/regions/jardim-paulista/banner.webp'),
	metaTags,
	info: {
		title: '',
		description: ''
	},
	questions: [
		{
			question: 'Onde fica o Jardim Paulista?',
			answer:
				'O Jardim Paulista está localizado na cidade de São Paulo, na zona Oeste, no distrito da Bela Vista, sob a subprefeitura da Sé. O bairro faz divisa com importantes áreas, como o Jardim América, o Jardim Europa e o Itaim Bibi. As principais avenidas de acesso são a Avenida Paulista, Avenida Brasil e a Avenida 9 de Julho, além de ruas estratégicas como a Rua José Maria Lisboa, proporcionando uma excelente infraestrutura e mobilidade.'
		},
		{
			question: 'Como chegar ao Jardim Paulista?',
			answer:
				'O Jardim Paulista é bem acessível tanto de transporte privado quanto público. Para quem utiliza transporte privado, o bairro é facilmente acessado pela Avenida Paulista, que conecta o Jardim Paulista a diversas regiões de São Paulo. Outras vias importantes, como a Avenida Brasil e a Avenida 9 de Julho, também oferecem acesso direto ao bairro. Para quem usa transporte público, o Jardim Paulista é bem servido por diversas linhas de ônibus, que circulam pelas principais avenidas da região. O bairro também é conectado por ciclovias que permitem fácil acesso ao Parque Ibirapuera e aos bairros vizinhos.'
		},
		{
			question: 'O que fazer no Jardim Paulista? ',
			answer:
				'O Jardim Paulista oferece diversas opções de lazer e cultura. O bairro é perfeito para quem busca entretenimento e tranquilidade ao mesmo tempo. Você pode explorar o Parque Trianon, que fica bem próximo à Avenida Paulista, e o Parque Ibirapuera, na outra ponta do bairro, com opções de atividades ao ar livre e museus. Além disso, o Jardim Paulista conta com clubes exclusivos, como a Sociedade Harmonia de Tênis e o Clube Athletico Paulistano, que oferecem atividades esportivas e sociais para todas as idades.'
		},
		{
			question: 'Onde comer no Jardim Paulista?',
			answer:
				'O Jardim Paulista é uma excelente escolha para quem ama gastronomia. O bairro conta com uma variedade de restaurantes, como o Osteria Generale, a Lellis Trattoria e o Tontoni Trattoria, que oferecem pratos italianos de qualidade. Se preferir uma culinária contemporânea, o Insalata é um dos destaques, servindo desde risotos até carnes e saladas. Para um lanche rápido, o Miski é uma ótima opção, especializado em esfihas. Para bons drinks, o Skye Bar no Hotel Unique oferece uma vista incrível da cidade, e o The Punch, na Avenida Paulista, oferece uma experiência exclusiva com coquetéis feitos sob medida pelos barmen.'
		}
	],
	hero: {
		title: regionName,
		description:
			'Onde a energia de São Paulo se encontra com o verde do parque do Ibirapuera.',
		backgroundImage: getUrlFromStaticDNS(
			'/images/regions/jardim-paulista/banner.webp'
		)
	},
	map: {
		title: `${regionName}: a vida no bairro badalado`,
		content: `Fazendo divisa com a principal avenida de São Paulo, a Avenida Paulista, e o parque do Ibirapuera, o ${regionName} é um dos bairros mais procurados da cidade por sua ótima localização, fácil mobilidade, opções de lazer e uma das melhores qualidades de vida de São Paulo.<br/><br/>Sim, sua extremidade pode até ser próxima de uma das avenidas mais movimentadas da cidade, mas isso não faz com que o ${regionName} seja um oásis no meio do caos de São Paulo. <br/><br/>Com ótimos restaurantes, bares, hotéis e até o recém inaugurado shopping Jardim Pamplona, o ${regionName} é perfeito para quem está de passagem por São Paulo ou quer encontrar um imóvel para chamar de lar.`,
		image: '/images/regions/jardim-paulista/map.webp',
		links: [
			{
				label: `Ver imóveis no ${regionName}`,
				url: PropertyFiltersPresenter.getPathFromFilters({
					propertyTypes: [],
					regions: [regionName],
					states: ['SP'],
					cities: ['São Paulo']
				})
			}
		]
	},
	content: [
		{
			info: {
				title: `Restaurantes no ${regionName}`,
				content: `A localização entre a Avenida Paulista e a pontinha da Avenida Brasil fazem com que o ${regionName} tenha uma variedade de restaurantes e bares para todos os gostos. Os fãs de comida italiana, por exemplo, tem cadeira cativa no bairro. Enquanto o Osteria Generale e o Lellis Trattoria são dois dos restaurantes mais tradicionais da cidade, formando filas aos finais de semana, o Tontoni Trattoria, na Alameda Joaquim Eugênio de Lima serve uma gastronomia italiana moderna e de renome, após atualização no cardápio. Para acabar um dia com toda a família, a pedida é a Veridiana Pizzaria, com uma unidade na Rua José Maria Lisboa.<br/><br/>Se você prefere uma gastronomia contemporânea mundial, o Insalata é o lugar ideal: servindo opções que vão de saladas a risotos e ótimas carnes, o restaurante vive lotado por um motivo: comida incrível e padrão impecável. Para levar para casa ou comer no local, o Miski é um segredo bem escondido entre a comunidade árabe de São Paulo. Aproveite para comer algumas das melhores esfihas da cidade.<br/><br/>Para bons drinks, o Skye Bar, dentro do Hotel Unique, oferece, além de uma carta extensa, uma ótima vista da cidade. Escondido em uma galeria perto da Avenida Paulista, o The Punch trabalha apenas com reservas e serve, por noite, 16 pessoas divididas em dois turnos. Inspirados na arte de fazer coquetéis dos japoneses, o diferencial da casa é sua falta de cardápio. Todos os drinks são feitos na hora, em papo com os premiados barmen e suas indicações, de acordo com o que você gosta. Uma experiência única na cidade.`,
				class: 'row-span-2'
			},
			images: [
				{
					url: '/images/regions/jardim-paulista/content/content-1.webp',
					credits: 'Kamran Aydinov'
				},
				{
					url: '/images/regions/jardim-paulista/content/content-2.webp',
					credits: 'Patrick Tomasso'
				},
				{
					url: '/images/regions/jardim-paulista/content/content-3.webp',
					credits: 'Daniel Ducci'
				}
			]
		},
		{
			info: {
				title: `Os clubes do ${regionName}`,
				content: `Fazendo divisa com o Jardim América, o ${regionName} conta com opções de clubes próximas ao bairro, como a Sociedade Harmonia de Tênis, conhecida por ser uma das mais exclusivas da cidade. Com apenas 4 mil sócios, o clube é focado na prática do tênis e conta com nove quadras de saibro.<br/><br/>Pertinho de lá, o Clube Athletico Paulistano, um dos mais tradicionais da cidade, reúne cerca de 5 mil sócios e conta com diversas atividades esportivas – como basquete, beach tennis, vôlei, tênis, badminton e mais –, duas bibliotecas, sapateado, ballet, piano e teatro, além de 13 restaurantes e bares. Conta com piscina, sauna, salões de festa, boates, espaços infantis, cinema e quadras.`
			},
			images: [
				{ url: '/images/regions/jardim-paulista/content/content-4.webp' }
			]
		},
		{
			info: {
				title: `Parques no ${regionName}`,
				content: `Localizado na Avenida Paulista e na pontinha do ${regionName}, o Parque Trianon é um respiro no meio do caos paulistano. Sua área verde é histórica e reúne diversas árvores centenárias, além de obras de arte espalhadas por sua extensão. Fundado em 1910, o parque é um dos locais que abriga a Mata Atlântica nativa da cidade.<br/><br/>Na outra ponta do ${regionName}, o Parque Ibirapuera é perfeito para quem quer um programa que dura o dia todo. Sejam em corridas em torno do lago, passando pelos festivais do Pavilhão Japonês até as exposições espalhadas pelos museus projetados por Oscar Niemeyer, não faltarão opções para aproveitar o fim de semana.`
			},
			images: [
				{ url: '/images/regions/jardim-paulista/content/content-5.webp' }
			]
		},
		{
			info: {
				title: `Imóvel icônico no ${regionName}`,
				content: `Reformado por Nildo José, este apartamento no ${regionName} é uma verdadeira casa de revista, sendo figurado na Elle Décor España. Com três suítes, ele conta com vista livre para o Parque Ibirapuera, o tornando algo único. A sala ampla é integrada com a varanda e a cozinha planejada com ilha, tudo isso com muita luz natural. Além disso, três suítes e home theater também fazem parte do imóvel, inteirinho equipado com ar condicionado.`
			},
			images: [
				{ url: '/images/regions/jardim-paulista/content/content-6.webp' },
				{
					url: '/images/regions/jardim-paulista/content/content-7.webp',
					credits: 'EC691',
					class: 'col-start-2'
				}
			]
		}
	],
	seo: {
		title: `Quem se interessou por ${regionName} também procurou por`,
		links: [
			{
				title: 'Tipo de imóvel',
				links: [
					{
						label: 'Apartamento',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.APARTMENT],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Cobertura',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.PENTHOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Duplex',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.DUPLEX],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Garden',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.GARDEN],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa de vila',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.VILLAGE_HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: 'Localização',
				links: [
					{
						label: 'Itaim Bibi, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Itaim Bibi']
						})
					},
					{
						label: 'Jardim Paulista, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim Paulista']
						})
					},
					{
						label: 'Vila Nova Conceição, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Vila Nova Conceição']
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Higienópolis, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Higienópolis']
						})
					},
					{
						label: 'Brooklin, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Brooklin']
						})
					},
					{
						label: 'Moema, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Moema']
						})
					}
				]
			}
		],
		discoveryContent: {
			heading: `Mais sobre o ${regionName}`,
			paragraphs: [
				'Conheça o Jardim Paulista, um dos bairros mais desejados de São Paulo, onde o estilo de vida sofisticado encontra uma localização privilegiada. Entre a Avenida Paulista e o Parque Ibirapuera, a região é conhecida por seus renomados restaurantes, como o clássico Lellis Trattoria e o moderno Tontoni Trattoria, além do Skye Bar, dentro do Hotel Unique, famoso por sua vista panorâmica.',
				'O bairro também oferece proximidade a clubes exclusivos como a Sociedade Harmonia de Tênis e o Clube Athletico Paulistano, que combina tradição com uma ampla gama de atividades esportivas e sociais. O Jardim Paulista ainda abriga áreas verdes como o Parque Trianon, um refúgio de mata atlântica no meio da cidade, e o icônico Parque Ibirapuera. Seus  imóveis refletem a sofisticação da área, com destaque para o condomínio Address Jardins, que oferece modernidade e conforto em espaços de alto padrão. O renomado Hotel Unique, com sua arquitetura icônica, também contribui para o charme da região, tornando o Jardim Paulista perfeito para quem busca um estilo de vida que une exclusividade e qualidade de vida incomparável.'
			]
		}
	},
	nearRegions: [
		{
			image: '/images/regions/itaim-bibi/banner.webp',
			name: 'Itaim Bibi',
			href: '/bairros/itaim-bibi'
		},
		{
			image: '/images/regions/vila-nova-conceicao/banner.webp',
			name: 'Vila Nova Conceição',
			href: '/bairros/vila-nova-conceicao'
		},
		{
			image: '/images/regions/jardim-america/banner.webp',
			name: 'Jardim América',
			href: '/bairros/jardim-america'
		},
		{
			image: '/images/regions/pinheiros/banner.webp',
			name: 'Pinheiros',
			href: '/bairros/pinheiros'
		},
		{
			image: '/images/regions/alto-de-pinheiros/banner.webp',
			name: 'Alto de Pinheiros',
			href: '/bairros/alto-de-pinheiros'
		},
		{
			image: '/images/regions/jardim-europa/banner.webp',
			name: 'Jardim Europa',
			href: '/bairros/jardim-europa'
		},
		{
			image: '/images/regions/jardim-paulistano/banner.webp',
			name: 'Jardim Paulistano',
			href: '/bairros/jardim-paulistano'
		}
	]
};

export default region;
